/** Line-height used for the badge text; must match .unit-badge__text in unitBadge.css. */
export const LINE_HEIGHT = 1.05;

export interface FitMeasurement {
    /** Rendered content width at the trial font size (px). */
    width: number;
    /** Rendered content height at the trial font size (px). */
    height: number;
    /** Rendered line count at the trial font size. */
    lines: number;
}

export interface FitFontOptions {
    minFont: number;
    maxFont: number;
    /** Usable inner box (px) the text must fit within. */
    boxW: number;
    boxH: number;
    /** Optional cap on wrapped lines. */
    maxLines?: number;
    /**
     * Measures the text at a given font size. Injected so this algorithm is pure and
     * testable; the component passes a DOM-measuring callback, tests pass a fake.
     */
    measure: (fontSize: number) => FitMeasurement;
}

/**
 * Binary-search the largest font size at which the measured text fits the box on both
 * axes and within the optional line cap. Returns a size in [minFont, maxFont]; if nothing
 * fits (even at minFont) it returns minFont (the label then clips rather than truncating).
 */
export function computeFitFontSize({
    minFont,
    maxFont,
    boxW,
    boxH,
    maxLines,
    measure,
}: FitFontOptions): number {
    let lo = minFont;
    let hi = maxFont;
    let best = minFont;
    for (let i = 0; i < 24 && hi - lo > 0.25; i++) {
        const mid = (lo + hi) / 2;
        const m = measure(mid);
        const fits =
            m.height <= boxH + 0.5 &&
            m.width <= boxW + 0.5 &&
            (!maxLines || m.lines <= maxLines);
        if (fits) {
            best = mid;
            lo = mid;
        } else {
            hi = mid;
        }
    }
    return best;
}
